'use client';

import { type RefObject, useCallback, useRef } from 'react';
import * as THREE from 'three';

/**
 * Renderer management hook
 * Manages Three.js renderer
 */
export function useRenderer(containerRef: RefObject<HTMLDivElement | null>) {
  const rendererRef = useRef<THREE.WebGLRenderer | undefined>(undefined);

  const initializeRenderer = useCallback(() => {
    if (!containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRect.width, containerRect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const existingCanvas = containerRef.current.querySelector('canvas');
    if (existingCanvas) {
      containerRef.current.removeChild(existingCanvas);
    }

    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    return renderer;
  }, [containerRef]);

  const updateRendererSize = useCallback(() => {
    if (!containerRef.current || !rendererRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    rendererRef.current.setSize(containerRect.width, containerRect.height);
    rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }, [containerRef]);

  const render = useCallback((scene: THREE.Scene, camera: THREE.Camera) => {
    if (rendererRef.current) {
      rendererRef.current.render(scene, camera);
    }
  }, []);

  const clearRenderer = useCallback(() => {
    if (rendererRef.current && containerRef.current) {
      const canvas = containerRef.current.querySelector('canvas');
      if (canvas) {
        containerRef.current.removeChild(canvas);
      }
      rendererRef.current.dispose();
      rendererRef.current = undefined;
    }
  }, [containerRef]);

  return {
    rendererRef,
    initializeRenderer,
    updateRendererSize,
    render,
    clearRenderer,
  };
}
